import {
    forwardRef,
    ForwardRefExoticComponent,
    memo,
    MemoExoticComponent,
    MouseEvent,
    RefAttributes,
    useCallback,
} from 'react';

import { Box } from '../Box/Box';
import { Grid } from '../Grid/Grid';
import { Controller, ItemButton } from './components';
import { ChildrenComponents, GridItem, MatrixPickerProps, MatrixPickerValue } from './MatrixPicker.types';

export const MatrixPicker = memo(
    forwardRef<HTMLDivElement, MatrixPickerProps>((props, ref) => {
        const {
            items,
            maxColumns = 5,
            value = [],
            onChange,
            name,
            invalid,
            required,
            disabled,
            qa,
            disabledArray,
        } = props;

        const columns = Math.min(maxColumns, items.length);

        const handleItemClick = useCallback(
            (itemValue: MatrixPickerValue, event: MouseEvent<HTMLButtonElement>) => {
                if (disabled) return;

                const newValue = value.includes(itemValue)
                    ? value.filter((value: string) => value !== itemValue)
                    : [...value, itemValue];

                onChange?.(newValue, event);
            },
            [value, onChange, disabled]
        );

        return (
            <Box bordered radius='xsmall' spacing='12'>
                <Grid
                    ref={ref}
                    gridTemplateColumns={`repeat(auto-fill, minmax(${100 / columns}%, 1fr))`}
                    role='group'
                    aria-required={required}
                    aria-invalid={invalid}
                    data-qa={qa}
                >
                    {items.map((item: GridItem) => (
                        <ItemButton
                            key={item.id}
                            label={item.name}
                            value={item.id}
                            name={name}
                            checked={value.includes(item.id)}
                            disabled={disabled ?? disabledArray?.includes(item.id)}
                            onClick={handleItemClick}
                        />
                    ))}
                </Grid>
            </Box>
        );
    })
) as MemoExoticComponent<ForwardRefExoticComponent<MatrixPickerProps & RefAttributes<HTMLDivElement>>> &
    ChildrenComponents;

MatrixPicker.Controller = Controller;
MatrixPicker.displayName = 'MatrixPicker';
